import { Modal } from '@klear/ui/components/modal'
import { DatePicker } from '@klear/ui/components/date-picker'
import { Label } from '@klear/ui/components/label'
import { KlearDate } from '@klear/date'
import { useState } from 'react'
import { trpc } from '@klear/ui/lib/trpc'
import { toast } from '@klear/ui/hooks/use-toast'
import { useOrderEventInvalidate } from '@klear/ui/hooks/use-order-event-invalidate'
import isError from 'lodash/isError'

interface MarkCompleteModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  offerId: number
  offerIdDisplay: string
}

export function MarkCompleteModal({ isOpen, onClose, onSuccess, offerId, offerIdDisplay }: MarkCompleteModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [completedAt, setCompletedAt] = useState<KlearDate>(KlearDate.today())
  const invalidateOrderEvents = useOrderEventInvalidate()

  const setCompletedAtMutation = trpc.invoiceOffer.setOfferCompletedAt.useMutation()

  const handleSubmit = async () => {
    setIsLoading(true)
    try {
      await setCompletedAtMutation.mutateAsync({
        offerId,
        completedAt: completedAt.toJSON()
      })
      await invalidateOrderEvents()
      toast({
        title: 'Offer marked as complete',
        description: `${offerIdDisplay} has been marked as completed.`
      })
      onSuccess?.()
      onClose()
    } catch (error) {
      toast({
        title: 'Error',
        description: isError(error) ? error.message : 'Failed to mark offer as complete',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      size="sm"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Mark Offer as Complete"
      description={`Set the completion date for ${offerIdDisplay}.`}
      onSubmit={handleSubmit}
      primaryText={isLoading ? 'Processing...' : 'Mark Complete'}
      primaryDisabled={isLoading}
      secondaryText="Cancel"
      secondaryDisabled={isLoading}
      handleSecondaryAction={onClose}
      handlePrimaryAction={handleSubmit}
    >
      <div className="space-y-2">
        <Label>Completed At</Label>
        <DatePicker value={completedAt} onChange={(date) => date && setCompletedAt(date)} disabled={isLoading} />
      </div>
    </Modal>
  )
}
